import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useEntity } from '@app/hooks/useEntity';
import { useInstructorStore } from '@app/store/entityStore';
import { ActionList, ActionListItem, Badge, Bullseye, Button, EmptyState, EmptyStateActions, EmptyStateBody, EmptyStateFooter, Icon, Label, MenuToggle, MenuToggleElement, PageSection, Pagination, PaginationVariant, SearchInput, Select, SelectOption, Split, SplitItem, Title, Toolbar, ToolbarContent, ToolbarFilter, ToolbarGroup, ToolbarItem, ToolbarLabel, ToolbarLabelGroup, ToolbarToggleGroup, Tooltip } from '@patternfly/react-core';
import { FilterIcon, SearchIcon, UserIcon } from '@patternfly/react-icons';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useFilterRestore } from '@app/hooks/useFilterRestore';
import { useRowRestore } from '@app/hooks/useRowRestore';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { DataLoadErrorRow } from '@app/components/DataLoadError';

const emptyFilters = (): Record<string, string[]> => ({ type: [], name: [], status: [], partner: [], skill: [] });

/** What comes back when the user returns from an instructor's detail page. */
interface InstructorsView {
  filters: Record<string, string[]>;
  category: string | number | undefined;
  sortIndex: number | null;
  sortDirection: 'asc' | 'desc' | null;
}

const Instructors: React.FunctionComponent = () => {
  const navigate = useNavigate();

  // --- Core State ---
  const {items: rows, error, retry} = useEntity(useInstructorStore);

  // --- UI State ---
  // The filters last applied here, kept only for the trip out to an instructor and back.
  const { restored, keep } = useFilterRestore<InstructorsView>('instructors');
  const { restoredPage, rememberRow, rowProps } = useRowRestore('instructors');
  const [page, setPage] = React.useState(restoredPage ?? 1);
  const { perPage, setPerPage } = useTablePreferences('instructors', 10);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = React.useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = React.useState(false);
  const [currentCategory, setCurrentCategory] = React.useState<string | number | undefined>(restored?.category ?? 'Name');

  // --- Sorting State ---
  // Restored along with the filters: the page number comes back too, and on a differently
  // sorted list that page holds different rows.
  const [activeSortIndex, setActiveSortIndex] = React.useState<number | null>(restored?.sortIndex ?? null);
  const [activeSortDirection, setActiveSortDirection] = React.useState<'asc' | 'desc' | null>(restored?.sortDirection ?? null);

  // --- Filter State ---
  const [filters, setFilters] = React.useState<Record<string, string[]>>(restored?.filters ?? emptyFilters());
  const [inputValue, setInputValue] = React.useState('');

  React.useEffect(() => {
    keep({ filters, category: currentCategory, sortIndex: activeSortIndex, sortDirection: activeSortDirection });
  }, [filters, currentCategory, activeSortIndex, activeSortDirection, keep]);

  // --- Dynamic Search Wrapper ---
  const applyFrontendFilters = (newFilters: Record<string, string[]>) => {
    beginLoading();
    setTimeout(() => {
      setFilters(newFilters);
      setPage(1);
      requestAnimationFrame(() => setTimeout(() => endLoading(), 0));
    }, 10);
  };

  // --- Derived State 1: Filtering ---
  const filteredRows = React.useMemo(() => {
    return rows.filter((row) => {
      const matchesName = filters.name?.length === 0 || filters.name?.some((name) => row.name.toLowerCase().includes(name.toLowerCase()));
      const matchesType = filters.type?.length === 0 || filters.type?.includes(row.type);
      const matchesStatus = filters.status?.length === 0 || filters.status?.includes(row.status);
      
      const matchesPartner = filters.partner?.length === 0 || filters.partner?.some((p) => row.partner?.name?.toLowerCase().includes(p.toLowerCase()));
      
      const matchesSkill = filters.skill?.length === 0 || filters.skill?.some((s) => {
        const searchString = s.toLowerCase();
        return row.skills?.some((skillItem) => 
          skillItem.course?.name?.toLowerCase().includes(searchString) || 
          skillItem.course?.sku?.toLowerCase().includes(searchString)
        );
      });
      
      return matchesName && matchesType && matchesStatus && matchesPartner && matchesSkill;
    });
  }, [rows, filters]);

  // --- Derived State 2: Sorting ---
  const sortedRows = React.useMemo(() => {
    if (activeSortIndex === null || activeSortDirection === null) return filteredRows;
    
    return [...filteredRows].sort((a, b) => {
      let aValue: string | number | boolean = '';
      let bValue: string | number | boolean = '';

      // UPDATED SORT INDICES: Partner added at index 1
      switch (activeSortIndex) {
        case 0: aValue = a.name?.toLowerCase() || ''; bValue = b.name?.toLowerCase() || ''; break;
        case 1: aValue = a.partner?.name?.toLowerCase() || ''; bValue = b.partner?.name?.toLowerCase() || ''; break; // NEW: Partner sorting
        case 2: aValue = a.type || ''; bValue = b.type || ''; break;
        case 3: aValue = a.status || ''; bValue = b.status || ''; break;
        case 4: aValue = a.rhnId || ''; bValue = b.rhnId || ''; break;
        case 5: aValue = a.certificationId || ''; bValue = b.certificationId || ''; break;
        case 6: aValue = a.rhcx ? 1 : 0; bValue = b.rhcx ? 1 : 0; break;
        case 7: aValue = a.email?.toLowerCase() || ''; bValue = b.email?.toLowerCase() || ''; break;
        case 8: aValue = a.redhatEmail?.toLowerCase() || ''; bValue = b.redhatEmail?.toLowerCase() || ''; break;
      }

      if (aValue < bValue) return activeSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return activeSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRows, activeSortIndex, activeSortDirection]);

  // --- Derived State 3: Pagination ---
  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return sortedRows.slice(startIdx, startIdx + perPage);
  }, [sortedRows, page, perPage]);

  // --- Event Handlers ---
  const onDeleteFilter = (category: string | ToolbarLabelGroup, id: string | ToolbarLabel) => {
    const key = String(category).toLowerCase();
    if (filters[key]) {
      applyFrontendFilters({ ...filters, [key]: filters[key].filter(fil => fil !== id) });
    } else {
      applyFrontendFilters(emptyFilters());
    }
  };

  const clearFilters = () => applyFrontendFilters(emptyFilters());

  const onTextSearch = (value: string) => {
    if (!value.trim()) return;
    const categoryKey = String(currentCategory).toLowerCase();
    const prevFilters = filters[categoryKey] || [];
    
    if (!prevFilters.includes(value.trim())) {
      applyFrontendFilters({ ...filters, [categoryKey]: [...prevFilters, value.trim()] });
      setInputValue('');
    }
  };

  const onStatusSelect = (event?: React.MouseEvent<Element, MouseEvent>, selection?: string | number) => {
    if (!event) return;
    const checked = (event.target as HTMLInputElement).checked;
    const currentStatus = filters.status || [];
    const newStatus = checked ? [...currentStatus, String(selection)] : currentStatus.filter(v => v !== String(selection));
    applyFrontendFilters({ ...filters, status: newStatus });
  };

  const onTypeSelect = (event?: React.MouseEvent<Element, MouseEvent>, selection?: string | number) => {
    applyFrontendFilters({ ...filters, type: [String(selection)] });
    setIsFilterDropdownOpen(false);
  };

  const getSortParams = (columnIndex: number) => ({
    sortBy: { index: activeSortIndex !== null ? activeSortIndex : undefined, direction: activeSortDirection !== null ? activeSortDirection : undefined, defaultDirection: 'asc' as const },
    onSort: (_event: React.MouseEvent, index: number, direction: 'asc' | 'desc') => { setActiveSortIndex(index); setActiveSortDirection(direction); },
    columnIndex
  });

  // --- Render Helpers ---
  const buildCategoryDropdown = () => (
    <ToolbarItem>
      <Select
        onSelect={(e, val) => { setCurrentCategory(val); setIsCategoryDropdownOpen(false); setInputValue(''); }}
        selected={currentCategory}
        isOpen={isCategoryDropdownOpen}
        onOpenChange={(isOpen) => setIsCategoryDropdownOpen(isOpen)}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)} isExpanded={isCategoryDropdownOpen} icon={<FilterIcon />} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
            {currentCategory}
          </MenuToggle>
        )}
      >
        <SelectOption value="Name">Name</SelectOption>
        <SelectOption value="Partner">Partner</SelectOption>
        <SelectOption value="Skill">Skill</SelectOption>
        <SelectOption value="Status">Status</SelectOption>
        <SelectOption value="Type">Type</SelectOption>
      </Select>
    </ToolbarItem>
  );

  const buildFilterDropdown = () => {
    const typeMenuItems = [
      <SelectOption key="FTE" value="FTE">FTE</SelectOption>,
      <SelectOption key="REDHAT" value="REDHAT">Red Hat</SelectOption>,
      <SelectOption key="PARTNER" value="PARTNER">Partner</SelectOption>,
      <SelectOption key="FREELANCER" value="FREELANCER">Freelancer</SelectOption>
    ];

    const statusMenuItems = ['ACTIVE', 'INACTIVE', 'PENDING', 'REDHAT', 'NOT_ENROLLED'].map(status => (
      <SelectOption hasCheckbox key={status} value={status} isSelected={filters.status?.includes(status)}>
        {status.replace(/_/g, ' ')}
      </SelectOption>
    ));

    return (
      <React.Fragment>
        <ToolbarFilter labels={filters.name || []} deleteLabel={onDeleteFilter} categoryName="Name" showToolbarItem={currentCategory === 'Name'}>
          <SearchInput aria-label="name filter" placeholder="Filter by name..." onChange={(_event, value) => setInputValue(value)} value={inputValue} onClear={() => setInputValue('')} onSearch={(_e, val) => onTextSearch(val)} />
        </ToolbarFilter>

        <ToolbarFilter labels={filters.partner || []} deleteLabel={onDeleteFilter} categoryName="Partner" showToolbarItem={currentCategory === 'Partner'}>
          <SearchInput aria-label="partner filter" placeholder="Filter by partner..." onChange={(_event, value) => setInputValue(value)} value={inputValue} onClear={() => setInputValue('')} onSearch={(_e, val) => onTextSearch(val)} />
        </ToolbarFilter>

        <ToolbarFilter labels={filters.skill || []} deleteLabel={onDeleteFilter} categoryName="Skill" showToolbarItem={currentCategory === 'Skill'}>
          <SearchInput aria-label="skill filter" placeholder="Filter by skill (SKU or name)..." onChange={(_event, value) => setInputValue(value)} value={inputValue} onClear={() => setInputValue('')} onSearch={(_e, val) => onTextSearch(val)} />
        </ToolbarFilter>
        
        <ToolbarFilter labels={filters.type} deleteLabel={onDeleteFilter} categoryName="Type" showToolbarItem={currentCategory === 'Type'}>
          <Select aria-label="Type" onSelect={onTypeSelect} selected={filters.type?.[0] || ''} isOpen={isFilterDropdownOpen} onOpenChange={(isOpen) => setIsFilterDropdownOpen(isOpen)} popperProps={{ minWidth: '100px' }} toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
              <MenuToggle ref={toggleRef} onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)} isExpanded={isFilterDropdownOpen} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
                {filters.type?.[0] || `Any`}
              </MenuToggle>
            )}>
            {typeMenuItems}
          </Select>
        </ToolbarFilter>

        <ToolbarFilter labels={filters.status} deleteLabel={onDeleteFilter} categoryName="Status" showToolbarItem={currentCategory === 'Status'}>
          <Select aria-label="Status" isOpen={isFilterDropdownOpen} onOpenChange={(isOpen) => setIsFilterDropdownOpen(isOpen)} popperProps={{ minWidth: '100px' }} onSelect={onStatusSelect} selected={filters.status} toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
              <MenuToggle ref={toggleRef} onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)} isExpanded={isFilterDropdownOpen} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
                Filter by status {filters.status && filters.status.length > 0 && <Badge isRead>{filters.status.length}</Badge>}
              </MenuToggle>
            )}>
            {statusMenuItems}
          </Select>
        </ToolbarFilter>
      </React.Fragment>
    );
  };

  const buildPagination = (variant: 'bottom' | 'top', isCompact: boolean) => (
    <Pagination
      isCompact={isCompact} itemCount={filteredRows.length} page={page} perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
      variant={variant as PaginationVariant} isStatic
    />
  );

  const renderLabel = (labelText: string) => {
    switch (labelText) {
      case 'ACTIVE': return <Label color="green">{labelText}</Label>;
      case 'PENDING': return <Label color="orange">{labelText}</Label>;
      case 'REDHAT': return <Label color="blue">{labelText}</Label>;
      case 'INACTIVE': return <Label color="red">{labelText}</Label>;
      case 'NOT_ENROLLED': return <Label color="grey">{labelText}</Label>;
      default: return <>{labelText}</>;
    }
  };

  return (
    <React.Fragment>
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Instructors</Title>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <Button variant="primary" onClick={() => navigate('/instructors/new')}>New Instructor</Button>
              </ActionListItem>
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      <Toolbar id="toolbar-with-chip-groups" clearAllFilters={clearFilters} collapseListedFiltersBreakpoint="xl">
        <ToolbarContent>
          <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
            <ToolbarGroup variant="filter-group" style={{ lineHeight: '22px', alignItems: 'center' }}>
              {buildCategoryDropdown()}
              {buildFilterDropdown()}
            </ToolbarGroup>
          </ToolbarToggleGroup>
          <ToolbarItem variant="pagination">{buildPagination('top', true)}</ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {/* Frozen Actions column: the inner container is the horizontal scroll it sticks to, and
          gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
      <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset))', minHeight: 0 }}>
        <InnerScrollContainer>
          <Table aria-label="Instructors" isStickyHeader variant='compact' gridBreakPoint="">
            <Thead>
              <Tr>
                <Th sort={getSortParams(0)}>Name</Th>
                <Th sort={getSortParams(1)}>Partner</Th>
                <Th sort={getSortParams(2)}>Type</Th>
                <Th sort={getSortParams(3)}>Status</Th>
                <Th sort={getSortParams(4)}>RHN ID</Th>
                <Th sort={getSortParams(5)}>Certification</Th>
                <Th sort={getSortParams(6)}>RHCX</Th>
                <Th sort={getSortParams(7)}>Email</Th>
                <Th sort={getSortParams(8)}>RH Email</Th>
                <Th width={10} isStickyColumn stickyMinWidth="90px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedRows.map((row) => (
                <Tr key={row.id} {...rowProps(row.id)}>
                  <Td dataLabel="Name">{row.name}</Td>
                  <Td dataLabel="Partner">{row.partner?.name || '-'}</Td>
                  <Td dataLabel="Type">{row.type}</Td>
                  <Td dataLabel="Status">{renderLabel(row.status)}</Td>
                  <Td dataLabel="RHN ID">{row.rhnId}</Td>
                  <Td dataLabel="Certification">
                    {row.certificationId && (
                      <Button variant='link' component="a" href={'https://rhtapps.redhat.com/verify/?certId=' + row.certificationId} target='_blank'>
                        {row.certificationId}
                      </Button>
                    )}
                  </Td>
                  <Td dataLabel="RHCX">{row.rhcx ? "Yes" : "No"}</Td>
                  <Td dataLabel="Email">{row.email}</Td>
                  <Td dataLabel="RH Email">{row.redhatEmail}</Td>
                  <Td dataLabel="Actions" modifier="nowrap"
                    isStickyColumn stickyMinWidth="90px" stickyRightOffset="0" hasLeftBorder>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                      <Tooltip content="View Instructor">
                        <Button icon={<Icon size='lg'><UserIcon /></Icon>} variant="plain" onClick={() => { rememberRow(row.id, page); navigate('/instructors/' + row.id); }} />
                      </Tooltip>
                      {/* <RemoveButton confirmationMessage='Confirm instructor removal?' onRemoveConfirm={(ev) => onRemoveConfirm(row.id, ev)} /> */}
                    </div>
                  </Td>
                </Tr>
              ))}
              {/* Nothing to show: distinguish "the load failed" from "no records match" */}
              {error && filteredRows.length === 0 && (
                <DataLoadErrorRow colSpan={10} error={error} entityLabel="instructors" onRetry={retry} />
              )}
              {!error && filteredRows.length === 0 && (
                <Tr>
                  <Td colSpan={10}>
                    <Bullseye>
                      <EmptyState headingLevel="h5" icon={SearchIcon} titleText="Clear all filters and try again.">
                        <EmptyStateBody>No results match the filter criteria. Clear all filters and try again.</EmptyStateBody>
                        <EmptyStateFooter>
                          <EmptyStateActions><Button variant="link" onClick={clearFilters}>Clear all filters</Button></EmptyStateActions>
                        </EmptyStateFooter>
                      </EmptyState>
                    </Bullseye>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </InnerScrollContainer>
      </OuterScrollContainer>
      {buildPagination('bottom', false)}
    </React.Fragment>
  );
};

export { Instructors };
